import React, { useCallback, useMemo, useState } from 'react';
import { isInitialQueryLoading } from '../../../lib/query/listQuery';
import { Button, Modal, Space, Tag, Typography } from 'antd';
import { toast } from '../../../lib/toast';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import DataTable from '../../../components/DataTable';
import { ListPage, buildCogActionsColumn } from '../../../components/list';
import { usePermissions } from '../../permissions/PermissionsContext';
import type { UserListItem } from '../../contacts/types';
import { useUsers } from '../hooks/useUsers';
import { useCreateUser } from '../hooks/useCreateUser';
import { useRevokeAllTokens } from '../hooks/useRevokeAllTokens';
import { useResendInvite } from '../hooks/useResendInvite';
import { useSendPasswordReset } from '../hooks/useSendPasswordReset';
import {
  accountStatusLabel,
  accountStatusTagColor,
  isActiveAccountStatus,
  isPendingAccountStatus,
} from '../utils/accountStatus';
import UserFormDrawer from './UserFormDrawer';
import UserEditDrawer from './UserEditDrawer';
import { useUpdateUser } from '../hooks/useUpdateUser';
import Can from '../../permissions/Can';

const { Text } = Typography;

const UserManagementPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);

  const { data: paged, isLoading: usersLoading } = useUsers({ pageNumber: page, pageSize });
  const isLoading = isInitialQueryLoading(usersLoading, paged);
  const createMutation = useCreateUser(() => {
    setDrawerOpen(false);
    toast.success('User created. Invite email sent.');
  });
  const revokeAllMutation = useRevokeAllTokens();
  const resendInviteMutation = useResendInvite();
  const sendPasswordResetMutation = useSendPasswordReset();
  const updateMutation = useUpdateUser(() => {
    setEditDrawerOpen(false);
    setEditingUserId(null);
  });
  const { hasPermission } = usePermissions();

  const handlePaginationChange = useCallback((newPage: number, newPageSize: number) => {
    setPage(newPage);
    setPageSize(newPageSize);
  }, []);

  const handleResendInvite = useCallback((user: UserListItem) => {
    Modal.confirm({
      title: 'Resend invitation?',
      content: (
        <>
          Send a new setup link to <strong>{user.email}</strong>? The previous invite link will no
          longer work.
        </>
      ),
      okText: 'Send email',
      cancelText: 'Cancel',
      onOk: () =>
        new Promise<void>((resolve, reject) => {
          resendInviteMutation.mutate(user.id, {
            onSuccess: () => resolve(),
            onError: () => reject(new Error('Failed')),
          });
        }),
    });
  }, [resendInviteMutation]);

  const handleRevokeAllTokens = useCallback((user: UserListItem) => {
    Modal.confirm({
      title: 'Revoke all sessions?',
      content: `This will log out ${user.displayName} from all devices.`,
      okText: 'Revoke',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: () =>
        new Promise<void>((resolve, reject) => {
          revokeAllMutation.mutate(user.id, {
            onSuccess: () => {
              toast.success('All sessions revoked for this user');
              resolve();
            },
            onError: () => {
              toast.error('Failed to revoke sessions');
              reject(new Error('Failed'));
            },
          });
        }),
    });
  }, [revokeAllMutation]);

  const columns: ColumnsType<UserListItem> = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'displayName',
        key: 'displayName',
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
      },
      {
        title: 'Status',
        dataIndex: 'accountStatus',
        key: 'accountStatus',
        render: (status: unknown) => (
          <Tag color={accountStatusTagColor(status)}>{accountStatusLabel(status)}</Tag>
        ),
      },
      {
        title: 'Roles',
        dataIndex: 'roleNames',
        key: 'roleNames',
        render: (roleNames: string[] | undefined) =>
          roleNames && roleNames.length > 0 ? (
            <Space size={[4, 4]} wrap>
              {roleNames.map((name) => (
                <Tag key={name}>{name}</Tag>
              ))}
            </Space>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: 'First name',
        dataIndex: 'firstName',
        key: 'firstName',
      },
      {
        title: 'Last name',
        dataIndex: 'lastName',
        key: 'lastName',
      },
      buildCogActionsColumn<UserListItem>({
        onEdit: (record) => {
          setEditingUserId(record.id);
          setEditDrawerOpen(true);
        },
        editPermission: 'users.edit',
        extraItems: (record) => {
          const pending = isPendingAccountStatus(record.accountStatus);
          const active = isActiveAccountStatus(record.accountStatus);
          const items = [];
          if (pending && hasPermission('users.edit')) {
            items.push({
              key: 'resend-invite',
              label: 'Resend invite',
              onClick: () => handleResendInvite(record),
            });
          }
          if (active) {
            items.push({
              key: 'send-reset',
              label: 'Send password reset email',
              onClick: () => sendPasswordResetMutation.mutate(record.id),
            });
          }
          items.push({
            key: 'revoke-all',
            label: 'Revoke all sessions',
            danger: true,
            onClick: () => handleRevokeAllTokens(record),
          });
          return items;
        },
        rowNumber: (_record, index) => (page - 1) * pageSize + index + 1,
      }),
    ],
    [
      handleResendInvite,
      handleRevokeAllTokens,
      hasPermission,
      sendPasswordResetMutation,
      page,
      pageSize,
    ],
  );

  const handleCreateUser = (values: {
    email: string;
    firstName: string;
    lastName: string;
    roleIds?: string[];
    customerSegmentIds?: string[];
  }) => {
    createMutation.mutate(values);
  };

  return (
    <>
      <ListPage
        title="Users"
        subtitle="Manage users and assign roles. Pending users must complete setup via the invitation email."
        actions={(
          <Can permission="users.create">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setDrawerOpen(true)}
            >
              Create user
            </Button>
          </Can>
        )}
      >
        <DataTable<UserListItem>
          columns={columns}
          dataSource={paged?.items ?? []}
          loading={isLoading}
          rowKey="id"
          bordered
          striped
          pagination={
            paged
              ? {
                  current: paged.pageNumber,
                  pageSize: paged.pageSize,
                  total: paged.totalCount,
                  onChange: handlePaginationChange,
                }
              : undefined
          }
          emptyText="No users yet."
        />
      </ListPage>

      <UserFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSave={handleCreateUser}
        saving={createMutation.isPending}
      />
      <UserEditDrawer
        open={editDrawerOpen}
        userId={editingUserId}
        onClose={() => {
          setEditDrawerOpen(false);
          setEditingUserId(null);
        }}
        onSave={(values) => {
          if (editingUserId) {
            updateMutation.mutate({
              userId: editingUserId,
              data: {
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                roleIds: values.roleIds,
                customerSegmentIds: values.customerSegmentIds,
              },
            });
          }
        }}
        saving={updateMutation.isPending}
      />
    </>
  );
};

export default UserManagementPage;
